import { useEffect, useState } from 'react';
import { MessageCircleIcon, RotateCwIcon, WifiOffIcon } from 'lucide-react';
import { salon, LOGO } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { Skeleton } from '../../ui/Skeleton';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { ProductGridSkeleton } from './ProductGridSkeleton';
type Props = {
  /** Foutmelding, of null tijdens het laden. */
  error: string | null;
  onRetry: () => void;
};

/**
 * Wat bezoekers zien zolang de collectie van de server komt, of als die niet bereikbaar is.
 * Laden: een skelet in de vorm van de winkel (geen leeg scherm, geen verspringing).
 * Fout: een uitleg met opnieuw proberen, en automatisch opnieuw zodra de verbinding terug is.
 */
export function CatalogFallback({
  error,
  onRetry
}: Props) {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);
  useEffect(() => {
    const goOnline = () => {
      setOffline(false);
      if (error) onRetry();
    };
    const goOffline = () => setOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [error, onRetry]);
  if (error) {
    return <main className="flex min-h-screen min-h-dvh w-full flex-col items-center justify-center bg-sand px-6 text-center" data-id="element-8821" data-mp="3t84zdmejdx9k">
        <img src={LOGO} alt={salon.name} className="h-12 w-auto mix-blend-multiply" data-id="element-8822" data-mp="6ytyxlnlx71y" />
        <div className="mt-10 max-w-sm" role="alert" data-id="element-8823" data-mp="6wzoj19yoxc4">
          {offline && <WifiOffIcon className="mx-auto mb-4 h-6 w-6 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8824" data-mp="kppc3a552abc" />}
          <h1 className="type-sub text-ink" data-id="element-8825" data-mp="gp5d01108wmk">{offline ? 'Je bent offline' : 'De winkel is even niet bereikbaar'}</h1>
          <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-8826" data-mp="umajg24mrxic">
            {offline ? 'Zodra je weer verbinding hebt, laden we de collectie vanzelf opnieuw.' : `${error.replace(/[.!]+$/, '')}. Probeer het zo nog eens — of stel je vraag direct via WhatsApp.`}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3" data-id="element-8827" data-mp="3ngirmzxvuixi">
            <button type="button" onClick={onRetry} className={btnPrimary} data-id="element-8828" data-mp="3sgezxbdm4wiv">
              <RotateCwIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8829" data-mp="14c5qacakwic" />
              Opnieuw proberen
            </button>
            <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnSecondary} data-id="element-8830" data-mp="64hrj9vvrosi">
              <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8831" data-mp="b3ktumkh7gws" />
              Stel je vraag
              <span className="sr-only" data-id="element-8832" data-mp="k1wkgolceep8">{shopLabels.viaWhatsApp}</span>
            </a>
          </div>
        </div>
      </main>;
  }
  return <div className="min-h-screen min-h-dvh w-full bg-sand" data-id="element-8833" data-mp="2gvq804vbftp4">
      <p role="status" className="sr-only" data-id="element-8834" data-mp="2lt9rbj6vewnx">
        {shopLabels.gridLoading}
      </p>
      <div className="h-9 bg-ink" aria-hidden="true" data-id="element-8835" data-mp="2ea0effz5ofu4" />
      <header className="border-b border-line" aria-hidden="true" data-id="element-8836" data-mp="2ebl9d6pdu0si">
        <div className="mx-auto flex h-20 max-w-content items-center justify-between px-6 lg:px-10" data-id="element-8837" data-mp="29j0h49m4jvr3">
          <img src={LOGO} alt="" className="h-10 w-auto mix-blend-multiply" data-id="element-8838" data-mp="24jfki5uffzhj" />
          <div className="hidden gap-7 lg:flex" data-id="element-8839" data-mp="1zle1tm0wy26l">
            {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} className="h-3 w-16" data-id="element-8840" data-mp="2x8uwaxtx1wei" />)}
          </div>
          <Skeleton className="h-10 w-10 rounded-full lg:w-32" data-id="element-8841" data-mp="2x6icsg8ifdzc" />
        </div>
      </header>
      <div className="mx-auto max-w-content px-6 pb-24 pt-10 lg:px-10" aria-hidden="true" data-id="element-8842" data-mp="2s7oxci9nxcwj">
        <Skeleton className="h-10 w-2/3 max-w-md" data-id="element-8843" data-mp="2ncrszonbvrpz" />
        <Skeleton className="mt-4 h-4 w-full max-w-lg" data-id="element-8844" data-mp="2j9v5bjixl5f6" />
        <div className="mt-8 flex gap-2" data-id="element-8845" data-mp="2jbfzh81c2mci">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-9 w-24 rounded-full" data-id="element-8846" data-mp="1bwutj6hv5pei" />)}
        </div>
        <div className="mt-10" data-id="element-8847" data-mp="17sdbbbr5jk5u">
          <ProductGridSkeleton data-id="element-8848" data-mp="2vqmgpbabp2we" />
        </div>
      </div>
    </div>;
}